import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import { contentApi } from '../lib/api';

export default function BlogIndexPage() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    contentApi.getBlogPosts()
      .then(r => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  return (
    <>
      <title>Kalaripayattu Writings & Wisdom | The Kalari Movement</title>
      <meta name="description" content="Essays on the philosophy, practice, and lineage of Kalaripayattu — written for those who seek depth, not trends." />
      <meta property="og:title" content="Kalaripayattu Writings & Wisdom | The Kalari Movement" />
      <meta property="og:description" content="Essays on the philosophy, practice, and lineage of Kalaripayattu — written for those who seek depth, not trends." />
      <meta property="og:url" content="https://thekalarimovement.com/blog" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://thekalarimovement.com/blog" />
      <div className="min-h-screen bg-[#FDFBF7]">
        <SiteNav />
        <div className="pt-16">
          <BlogHero />
          <BlogGrid posts={posts} />
        </div>
        <BlogFooter />
      </div>
    </>
  );
}

function BlogHero() {
  return (
    <div className="bg-[#1C1917] py-20 px-6 text-center">
      <p className="text-[#DDA15E] text-xs font-semibold uppercase tracking-widest mb-4">The Kalari Movement</p>
      <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">Writings & Wisdom</h1>
      <p className="text-white/60 max-w-xl mx-auto">
        Essays on the philosophy, practice, and lineage of Kalaripayattu — written for those who seek depth.
      </p>
    </div>
  );
}

function BlogGrid({ posts }) {
  if (!posts) {
    return (
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-2xl overflow-hidden animate-pulse bg-[#E7E5E4] h-80" />
          ))}
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return (
      <section className="py-32 text-center">
        <p className="text-[#A8A29E] text-lg">No posts published yet. Check back soon.</p>
      </section>
    );
  }

  return (
    <section className="py-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map(post => <BlogCard key={post._id} post={post} />)}
      </div>
    </section>
  );
}

function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-white rounded-2xl overflow-hidden border border-[#E7E5E4] hover:shadow-lg transition-all duration-300 flex flex-col"
    >
      {post.hero_image_url ? (
        <div className="aspect-[16/9] overflow-hidden bg-[#F4EFE6]">
          <img
            src={post.hero_image_url}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      ) : (
        <div className="aspect-[16/9] bg-gradient-to-br from-[#1C1917] to-[#9A3B26]" />
      )}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {post.category && (
            <span className="text-xs font-semibold text-[#9A3B26] bg-[#F4EFE6] px-2 py-0.5 rounded-full">
              {post.category}
            </span>
          )}
          {post.read_time_mins && (
            <span className="text-xs text-[#A8A29E] flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.read_time_mins} min read
            </span>
          )}
        </div>
        <h2 className="font-heading text-xl font-bold text-[#292524] mb-2 group-hover:text-[#9A3B26] transition-colors leading-snug">
          {post.title}
        </h2>
        <p className="text-[#78716C] text-sm leading-relaxed flex-1 line-clamp-3">{post.excerpt}</p>
        <div className="mt-5 flex items-center gap-1 text-[#9A3B26] text-sm font-semibold">
          Read more <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

function BlogFooter() {
  return (
    <footer className="bg-[#141210] text-white/50 py-8 px-6 text-center text-sm">
      © {new Date().getFullYear()} The Kalari Movement ·{' '}
      <a href="mailto:connect@thekalarimovement.com" className="hover:text-white transition-colors">
        connect@thekalarimovement.com
      </a>
    </footer>
  );
}
